import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useClubs } from '../../../hooks/clubs/useClubs'
import { useCourtsByClub } from '../../../hooks/court/useCourtsByClub'

import { Tabs } from '../../../components/tabs/Tabs'
import Loading from '../../../components/loading/Loading'
import GuestClubSection from '../../../components/guest/guestClubSection/GuestClubSection'

import { useApp } from '../../../contexts/AppContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { ClubModelMetadata, CourtModel } from '../../../types/court/court'

const GuestClubs = () => {
  const { showBanner } = useApp()
  const { t } = useTranslation('clubs')
  const clubsHook = useClubs()
  const courtsHook = useCourtsByClub()
  const [clubs, setClubs] = useState<ClubModelMetadata[]>([])
  const [courts, setCourts] = useState<CourtModel[]>([])
  const [selectedClubId, setSelectedClubId] = useState<number | null>(null)
  const [isLoadingClubs, setIsLoadingClubs] = useState(true)
  const [isLoadingCourts, setIsLoadingCourts] = useState(false)

  useEffect(() => {
    let active = true

    const loadClubs = async () => {
      try {
        const response = await clubsHook.clubs()
        const clubList =
          (response?.content as ClubModelMetadata[] | undefined) ?? []

        if (!active) {
          return
        }

        setClubs(clubList)
        setSelectedClubId(current =>
          current != null && clubList.some(club => club.id === current)
            ? current
            : (clubList[0]?.id ?? null)
        )
      } finally {
        if (active) {
          setIsLoadingClubs(false)
        }
      }
    }

    void loadClubs()

    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (selectedClubId == null) {
      setCourts([])
      return
    }

    let active = true

    const loadCourts = async () => {
      setIsLoadingCourts(true)
      setCourts([])

      try {
        const courtList = (await courtsHook.courtsByClub(selectedClubId)) ?? []

        if (!active) {
          return
        }
        setCourts(courtList)
      } finally {
        if (active) {
          setIsLoadingCourts(false)
        }
      }
    }

    void loadCourts()

    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClubId])

  useEffect(() => {
    const error = clubsHook.error || courtsHook.error
    if (!error) {
      return
    }

    showBanner({ kind: BannerKind.ERR, msg: error })
    clubsHook.resetState()
    courtsHook.resetState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubsHook.error, courtsHook.error])

  const selectedClub = clubs.find(club => club.id === selectedClubId) ?? null

  const selectClub = (clubId: number) => {
    if (clubId === selectedClubId) {
      return
    }

    setCourts([])
    setIsLoadingCourts(true)
    setSelectedClubId(clubId)
  }

  if (isLoadingClubs) {
    return (
      <div className="guest-clubs">
        <Loading />
      </div>
    )
  }

  if (!selectedClub) {
    return (
      <div className="guest-clubs">
        <p className="empty">{t('clubs.guest.empty')}</p>
      </div>
    )
  }

  const hasMultipleClubs = clubs.length > 1

  return (
    <div className="guest-clubs">
      {hasMultipleClubs && (
        <nav aria-label={t('clubs.guest.switcher.label')}>
          <Tabs
            className="guest-clubs-switch"
            tab={String(selectedClub.id)}
            tabs={clubs.map(club => ({
              value: String(club.id),
              label: club.name,
            }))}
            onChange={value => selectClub(Number(value))}
          />
        </nav>
      )}

      <GuestClubSection
        key={selectedClub.id}
        club={selectedClub}
        courts={courts}
        courtsLoading={isLoadingCourts}
      />
    </div>
  )
}

export default GuestClubs
